<template>
  <div class="modal-overlay" @click.self="$emit('cancel')">
    <div class="modal">
      <h3>Avsluta pass</h3>

      <div class="field-block">
        <label>Namn på passet</label>
        <input v-model="name" class="text-input" placeholder="t.ex. Kvällspass" />
      </div>

      <div class="field-block">
        <label>Total tid</label>
        <div class="duration-row">
          <button class="dur-btn" @click="adjustMinutes(-5)">−5</button>
          <div class="dur-value">
            <input type="number" inputmode="numeric" v-model.number="minutes" :min="minMinutes" class="dur-input" />
            <span>min</span>
          </div>
          <button class="dur-btn" @click="adjustMinutes(5)">+5</button>
        </div>
        <p v-if="minSeconds > 0" class="hint">
          Minst {{ minMinutes }} min pga tidsbaserade set ({{ formatDuration(minSeconds) }}).
        </p>
        <p v-if="belowFloor" class="hint err">Tiden kan inte vara kortare än {{ minMinutes }} min.</p>
      </div>

      <div class="field-block">
        <label>Kaloriberäkning</label>
        <div class="mode-toggle">
          <button :class="['mode-btn', { active: mode === 'auto' }]" @click="mode = 'auto'">Jämn fördelning</button>
          <button :class="['mode-btn', { active: mode === 'custom' }]" @click="enableCustom">Anpassa per övning</button>
        </div>

        <div v-if="!bodyWeightKg" class="hint">
          Ange din kroppsvikt under <strong>Profil</strong> för att uppskatta kalorier.
        </div>

        <div v-if="mode === 'custom'" class="alloc-list">
          <div v-for="ex in exercises" :key="ex.id" class="alloc-row">
            <span class="alloc-name">{{ ex.exerciseType.name }}</span>
            <div class="alloc-input">
              <input type="number" inputmode="numeric" v-model.number="allocMinutes[ex.id]"
                     :min="floorMinutes(ex)" class="dur-input sm" />
              <span>min</span>
            </div>
            <span class="alloc-cal">{{ bodyWeightKg ? Math.round(perExerciseCals[ex.id] || 0) + ' kcal' : '–' }}</span>
          </div>
        </div>
      </div>

      <div class="calorie-summary">
        <span class="cal-label">Uppskattat</span>
        <span class="cal-value">{{ bodyWeightKg ? Math.round(totalCals) + ' kcal' : '– kcal' }}</span>
      </div>

      <div class="modal-actions">
        <button class="btn-ghost" @click="$emit('cancel')">Avbryt</button>
        <button class="btn-save" :disabled="belowFloor" @click="confirm">Spara pass</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { formatDuration, computeCalories, minDurationSeconds } from '../../services/gymHelpers.js';

export default {
  name: 'CompleteSessionModal',
  props: {
    session: { type: Object, required: true },
    exercises: { type: Array, default: () => [] },
    bodyWeightKg: { type: Number, default: null },
    elapsedSeconds: { type: Number, default: 0 },
  },
  emits: ['confirm', 'cancel'],
  setup(props, { emit }) {
    const name = ref(props.session.name || '');
    const minSeconds = computed(() => minDurationSeconds(props.exercises));
    const minMinutes = computed(() => Math.ceil(minSeconds.value / 60));

    const minutes = ref(Math.max(minMinutes.value, Math.round(props.elapsedSeconds / 60) || minMinutes.value));
    const durationSeconds = computed(() => Math.round((minutes.value || 0) * 60));
    const belowFloor = computed(() => durationSeconds.value < minSeconds.value);
    function adjustMinutes(d) { minutes.value = Math.max(minMinutes.value, (minutes.value || 0) + d); }

    const mode = ref('auto');
    const allocMinutes = reactive({});
    function floorMinutes(ex) {
      const f = (ex.sets || []).reduce((s, st) => s + (st.durationSeconds || 0), 0);
      return Math.ceil(f / 60);
    }
    function enableCustom() {
      mode.value = 'custom';
      // Seed with the current even split so the user starts from a sensible baseline.
      const split = computeCalories(props.exercises, props.bodyWeightKg || 1, durationSeconds.value, {});
      props.exercises.forEach(ex => {
        if (allocMinutes[ex.id] == null) {
          allocMinutes[ex.id] = Math.max(floorMinutes(ex), Math.round((split.perExercise[ex.id]?.seconds || 0) / 60));
        }
      });
    }

    const allocationsMap = computed(() => {
      if (mode.value !== 'custom') return {};
      const m = {};
      props.exercises.forEach(ex => { m[ex.id] = Math.round((allocMinutes[ex.id] || 0) * 60); });
      return m;
    });

    const calc = computed(() =>
      computeCalories(props.exercises, props.bodyWeightKg, durationSeconds.value, allocationsMap.value));
    const totalCals = computed(() => calc.value.total);
    const perExerciseCals = computed(() => {
      const out = {};
      props.exercises.forEach(ex => { out[ex.id] = calc.value.perExercise[ex.id]?.calories || 0; });
      return out;
    });

    // Keep minutes at/above the floor if timed sets change.
    watch(minMinutes, mm => { if (minutes.value < mm) minutes.value = mm; });

    function confirm() {
      if (belowFloor.value) return;
      const allocations = mode.value === 'custom'
        ? props.exercises.map(ex => ({ sessionExerciseId: ex.id, allocatedSeconds: Math.round((allocMinutes[ex.id] || 0) * 60) }))
        : props.exercises.map(ex => ({ sessionExerciseId: ex.id, allocatedSeconds: null }));
      emit('confirm', {
        name: name.value.trim() || props.session.name,
        durationSecondsOverride: durationSeconds.value,
        allocations,
      });
    }

    return {
      name, minutes, minSeconds, minMinutes, belowFloor, adjustMinutes,
      mode, allocMinutes, floorMinutes, enableCustom,
      totalCals, perExerciseCals, confirm, formatDuration,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.55); backdrop-filter: blur(3px);
  display: flex; align-items: flex-end; justify-content: center; z-index: 1300; padding: 0;
}
.modal {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-bottom: none;
  border-radius: var(--gym-radius-lg) var(--gym-radius-lg) 0 0; padding: 1.25rem;
  width: 100%; max-width: 520px; max-height: 92vh; overflow-y: auto;
  display: flex; flex-direction: column; gap: 0.9rem; box-shadow: var(--gym-shadow);
}
.modal h3 { margin: 0; font-size: 1.2rem; color: var(--gym-text); }

.field-block { display: flex; flex-direction: column; gap: 0.4rem; }
.field-block > label { font-size: 0.85rem; font-weight: 600; color: var(--gym-text-muted); }
.text-input {
  padding: 0.65rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 1rem;
  background: var(--gym-surface-2); color: var(--gym-text);
}
.text-input:focus { outline: none; border-color: var(--gym-accent); box-shadow: 0 0 0 3px var(--gym-accent-soft); }

.duration-row { display: flex; align-items: center; gap: 0.6rem; }
.dur-btn {
  width: 44px; height: 44px; border-radius: var(--gym-radius); border: 1px solid var(--gym-border);
  background: var(--gym-surface-2); font-size: 1.05rem; font-weight: 700; color: var(--gym-accent); cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease);
}
.dur-btn:hover { background: var(--gym-surface-3); }
.dur-value { display: flex; align-items: baseline; gap: 0.35rem; flex: 1; justify-content: center; }
.dur-input {
  width: 80px; text-align: center; padding: 0.5rem; border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-sm); font-size: 1.2rem; font-weight: 700; color: var(--gym-text);
  background: var(--gym-surface-2);
}
.dur-input.sm { width: 64px; font-size: 1rem; }
.dur-value span { color: var(--gym-text-muted); font-size: 0.9rem; }

.hint { font-size: 0.78rem; color: var(--gym-text-muted); margin: 0; }
.hint.err { color: var(--gym-danger); }

.mode-toggle { display: flex; gap: 0.4rem; }
.mode-btn {
  flex: 1; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm);
  padding: 0.55rem; font-size: 0.85rem; cursor: pointer; color: var(--gym-text-muted);
  transition: background var(--gym-dur) var(--gym-ease);
}
.mode-btn.active { background: var(--gym-accent); border-color: var(--gym-accent); color: var(--gym-on-accent); font-weight: 600; }

.alloc-list { display: flex; flex-direction: column; gap: 0.4rem; margin-top: 0.5rem; }
.alloc-row { display: flex; align-items: center; gap: 0.6rem; }
.alloc-name { flex: 1; font-size: 0.9rem; color: var(--gym-text); }
.alloc-input { display: flex; align-items: baseline; gap: 0.3rem; }
.alloc-input span { font-size: 0.78rem; color: var(--gym-text-muted); }
.alloc-cal { width: 72px; text-align: right; font-size: 0.85rem; color: var(--gym-warning); font-weight: 700; }

.calorie-summary {
  display: flex; align-items: center; justify-content: space-between;
  background: var(--gym-warning-soft); border: 1px solid color-mix(in srgb, var(--gym-warning) 45%, transparent);
  border-radius: var(--gym-radius); padding: 0.75rem 1rem;
}
.cal-label { font-size: 0.85rem; color: var(--gym-warning); font-weight: 700; }
.cal-value { font-size: 1.4rem; font-weight: 800; color: var(--gym-warning); }

.modal-actions { display: flex; gap: 0.6rem; margin-top: 0.25rem; }
.btn-ghost {
  flex: 1; background: var(--gym-surface-2); border: 1px solid var(--gym-border); color: var(--gym-text-muted);
  border-radius: var(--gym-radius); padding: 0.75rem; font-size: 0.95rem; cursor: pointer;
}
.btn-ghost:hover { background: var(--gym-surface-3); color: var(--gym-text); }
.btn-save {
  flex: 2; background: var(--gym-accent); border: none; color: var(--gym-on-accent);
  border-radius: var(--gym-radius); padding: 0.75rem; font-size: 0.95rem; font-weight: 700; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease);
}
.btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-save:hover:not(:disabled) { background: var(--gym-accent-hover); }

@media (min-width: 560px) {
  .modal-overlay { align-items: center; }
  .modal { border-radius: var(--gym-radius-lg); border-bottom: 1px solid var(--gym-border); }
}
</style>
